import { useEffect } from "react";
import { isEmbedMode } from "./useEmbedMode";

// Auto-height for embed mode. A cross-origin parent cannot read the iframe
// content height, so the form reports it. This hook posts the content height to
// the parent window, and re-posts whenever the height changes. The parent then
// sets the iframe element height, so the frame always matches the form and the
// iframe never needs its own scrollbar.
//
// The host side is public/embed.js (a drop-in script) or a short listener. See
// docs/embedding.md for the host guide.

// One shared name for the message channel. The host filters on these strings.
export const EMBED_MESSAGE_SOURCE = "calibrite-embed";
export const EMBED_RESIZE_TYPE = "calibrite:resize";
export const EMBED_REQUEST_TYPE = "calibrite:requestHeight";
const EMBED_SCOPE = "newsletter-signup";

// The parent origin to post to. The default is "*": the height is not secret,
// and the host must still check event.origin on its side. ?parentOrigin= locks
// the target origin for a strict host. This matches the ?maxw= pattern in
// useEmbedMode.js — read with URLSearchParams inside try/catch, warn on invalid.
const getParentOrigin = () => {
  try {
    const raw = (
      new URLSearchParams(window.location.search).get("parentOrigin") || ""
    ).trim();
    if (!raw) return "*";
    return new URL(raw).origin;
  } catch {
    console.warn(
      'Ignoring parentOrigin — expected a full origin, e.g. "https://www.calibrite.com".'
    );
    return "*";
  }
};

// ?resize=0 forces the old fixed-height behaviour. The hook then posts nothing,
// and the host keeps a fixed iframe height with the inner scroll safety net.
export const isEmbedAutoHeightEnabled = () => {
  try {
    if (!isEmbedMode()) return false;
    return new URLSearchParams(window.location.search).get("resize") !== "0";
  } catch {
    return false;
  }
};

// Measure the content, not the viewport. document.documentElement.scrollHeight
// is clamped to the iframe height, so it reports the frame height when the frame
// is taller than the content — the height could then never shrink. body is
// height:auto in embed mode (style.css), so body.scrollHeight tracks the content
// up and down. Round up to avoid a 1px rounding scrollbar in the host frame.
const measureHeight = () => {
  try {
    const body = document.body;
    return body ? Math.ceil(body.scrollHeight) : 0;
  } catch {
    return 0;
  }
};

export default function useEmbedAutoHeight() {
  useEffect(() => {
    if (!isEmbedAutoHeightEnabled()) return undefined;

    const targetOrigin = getParentOrigin();
    let lastHeight = 0;

    // force=true always posts. Otherwise post only on a real height change, so a
    // ResizeObserver that fires for width alone does not flood the parent.
    const post = (force) => {
      try {
        if (window.parent === window) return; // not framed
        const height = measureHeight();
        if (!height) return;
        if (!force && height === lastHeight) return;
        lastHeight = height;
        window.parent.postMessage(
          {
            source: EMBED_MESSAGE_SOURCE,
            type: EMBED_RESIZE_TYPE,
            height,
            scope: EMBED_SCOPE,
          },
          targetOrigin
        );
      } catch (e) {
        console.warn("Embed height post failed.", e);
      }
    };

    // Answer a parent that asks for the height. This covers the race where the
    // parent starts to listen after the first post.
    const onMessage = (event) => {
      try {
        if (event.data && event.data.type === EMBED_REQUEST_TYPE) post(true);
      } catch {
        // ignore a malformed message
      }
    };
    window.addEventListener("message", onMessage);

    // Re-post on any content height change: a step change, validation text, a
    // language change, a font load, or a frame width change. Observe body, not
    // documentElement: body tracks the content and does not change when the host
    // resizes the frame, so this cannot feed back on our own height posts.
    let observer;
    try {
      observer = new ResizeObserver(() => post(false));
      if (document.body) observer.observe(document.body);
    } catch (e) {
      console.warn("ResizeObserver unavailable; posting the height once.", e);
    }

    // First post after the browser has laid the form out.
    let raf = 0;
    try {
      raf = window.requestAnimationFrame(() => post(true));
    } catch {
      post(true);
    }

    return () => {
      window.removeEventListener("message", onMessage);
      if (observer) {
        try {
          observer.disconnect();
        } catch {
          // ignore
        }
      }
      try {
        window.cancelAnimationFrame(raf);
      } catch {
        // ignore
      }
    };
  }, []);
}
